// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import http from "@http";
import { getSchema } from "@utils/schema";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  NonLinearWizard,
  Step,
} from "@components";
import Step1SpecialContactType from "./step1-special-contact-type";
import Step2SelectEmployee from "./step2-select-employee";
import Step3HierarchyScope from "./step3-hierarchy-scope";
import type {
  SpecialContact,
  SpecialContactEmployeeSearch,
} from "@modules/service/reducer/special-contacts/types";
import type { SpecialContactsType } from "@modules/service/reducer/special-contacts-types/types";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  current: ?SpecialContact,
  types: Array<SpecialContactsType>,
  employees: Array<SpecialContactEmployeeSearch>,
  hierarchy: ?HierarchyTree,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeList: typeof actionCreators.GetSpecialContactsEmployees,
  clearEmployeeList: typeof actionCreators.ClearSpecialContactsEmployees,
  setCurrent: typeof actionCreators.SetCurrentSpecialContact,
  update: typeof actionCreators.UpdateSpecialContact,
  setShowModal: typeof actionCreators.SetShowEditSpecialContactModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class SpecialContactsEditModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    const schema = getSchema("SpecialContactDTO");
    this.validate = new Ajv().compile(schema);
  }

  get isValid() {
    return this.validate ? this.validate(this.props.current) : false;
  }

  handleCancel = () => {
    this.props.setShowModal(false);
  };

  handleSubmit = () => {
    if (!this.props.current) return;
    this.props.update({
      clientId: this.props.clientId,
      specialContactId: this.props.current.id,
      data: this.props.current,
    });
  };

  handleChange = ([step1, step2, step3]: Array<Object> = []) => {
    this.props.setCurrent({
      ...this.props.current,
      type: step1,
      employee: step2,
      organisationalUnit: step3,
    });
  };

  get stepData() {
    const { type, employee, organisationalUnit } = this.props.current || {};
    return [
      {
        data: type,
        valid: true,
      },
      {
        data: employee,
        valid: true,
      },
      {
        data: organisationalUnit,
        valid: true,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EDIT_SPECIAL_CONTACT")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <NonLinearWizard
            defaultStep={this.props.defaultStep}
            data={this.stepData}
            onChange={this.handleChange}
            onSubmit={this.handleSubmit}
          >
            <Step
              component={Step1SpecialContactType}
              title={this.props.t("ADD_SPECIAL_CONTACT_STEP1")}
              rightInfo={item => (item || {}).name}
              types={this.props.types}
            />
            <Step
              component={Step2SelectEmployee}
              title={this.props.t("ADD_SPECIAL_CONTACT_STEP2")}
              rightInfo={item => (item || {}).name}
              clientId={this.props.clientId}
              employeeList={this.props.employees}
              getEmployeeList={this.props.getEmployeeList}
              clearEmployeeList={this.props.clearEmployeeList}
            />
            <Step
              component={Step3HierarchyScope}
              title={this.props.t("ADD_SPECIAL_CONTACT_STEP3")}
              rightInfo={item => (item || {}).name}
              hierarchy={this.props.hierarchy}
            />
          </NonLinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button disabled={!this.isValid} onClick={this.handleSubmit} materialIcon="save">
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    current: serviceSelectors.getCurrentSpecialContact(state),
    types: serviceSelectors.getSpecialContactsTypes(state),
    employees: serviceSelectors.getSpecialContactsEmployeesList(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeList: actionCreators.GetSpecialContactsEmployees,
  clearEmployeeList: actionCreators.ClearSpecialContactsEmployees,
  setCurrent: actionCreators.SetCurrentSpecialContact,
  update: actionCreators.UpdateSpecialContact,
  setShowModal: actionCreators.SetShowEditSpecialContactModal,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(SpecialContactsEditModal);
